import { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { patchReport } from '@/lib/api';
import type { Report, ReportStatus } from '@/lib/api';

export interface ReportEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: Report | null;
  /** Called after successful patch — parent should refresh */
  onSaved: () => void;
}

export function ReportEditModal({ open, onOpenChange, report, onSaved }: ReportEditModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ReportStatus>('draft');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && report) {
      setTitle(report.title);
      setDescription(report.description ?? '');
      setStatus(report.status);
      setError(null);
    }
  }, [open, report]);

  if (!report) return null;

  const dirty =
    title.trim() !== report.title ||
    (description.trim() || null) !== report.description ||
    status !== report.status;

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Tiêu đề không được trống');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await patchReport(report.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        status,
      });
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Chỉnh sửa báo cáo</DialogTitle>
          <DialogDescription>
            Cập nhật metadata. Hãng và period là immutable — nếu cần đổi, hãy tạo báo cáo mới.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 rounded-md bg-muted/40 p-3 text-xs">
            <div>
              <div className="text-muted-foreground">Hãng</div>
              <div className="font-semibold">{report.brand}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Period</div>
              <div className="font-semibold">{report.period_label}</div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="edit-title" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Tiêu đề <span className="text-destructive">*</span>
            </label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              disabled={submitting}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="edit-desc" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Mô tả
            </label>
            <Textarea
              id="edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              className="min-h-[80px]"
              disabled={submitting}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
            <Select value={status} onValueChange={(v) => setStatus(v as ReportStatus)} disabled={submitting}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft (chưa share được)</SelectItem>
                <SelectItem value="published">Published (sẵn sàng share)</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Hủy
          </Button>
          <Button onClick={handleSubmit} disabled={!dirty || submitting || !title.trim()}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Lưu thay đổi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
